import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search, RotateCcw } from 'lucide-react';

const NODE_COLORS = {
  // Core graph types
  Person: '#f97316',         // orange
  AbstractConcept: '#8b5cf6', // violet
  ContentAtom: '#3b82f6',    // blue
  HeartbeatInsight: '#10b981', // emerald
  Primitive: '#ec4899',       // pink
  Tension: '#f59e0b',         // amber
  // System entity types
  RootNode: '#ef4444',        // red — intelligence boundary
  Agent: '#06b6d4',           // cyan
  Organization: '#14b8a6',    // teal
  Business: '#0ea5e9',        // sky
  Product: '#a78bfa',         // violet-light
  Project: '#34d399',         // emerald-light
  Persona: '#f472b6',         // pink-light
  ResearchTopic: '#fbbf24',   // amber-light
  Decision: '#fb923c',        // orange-light
  ReasoningTrace: '#94a3b8',  // slate
  Session: '#a3e635',         // lime
  Topic: '#c084fc',           // purple
  Milestone: '#2dd4bf',       // teal-light
  Insight: '#10b981',         // emerald (same as HeartbeatInsight)
  Question: '#facc15',        // yellow
  Source: '#38bdf8',          // sky-light
};

// Fallback palette for custom/unknown entity types
const ENTITY_COLORS = [
  '#06b6d4', '#14b8a6', '#0ea5e9', '#a78bfa',
  '#f472b6', '#34d399', '#fbbf24', '#fb923c',
];

const NODE_TYPE_LABELS = {
  // Core graph types
  Person: 'People',
  AbstractConcept: 'Concepts',
  ContentAtom: 'Content',
  HeartbeatInsight: 'Insights',
  Primitive: 'Primitives',
  Tension: 'Tensions',
  // System entity types
  RootNode: 'Root Nodes',
  Agent: 'Agents',
  Organization: 'Organizations',
  Business: 'Businesses',
  Product: 'Products',
  Project: 'Projects',
  Persona: 'Personas',
  ResearchTopic: 'Research Topics',
  Decision: 'Decisions',
  ReasoningTrace: 'Reasoning Traces',
  Session: 'Sessions',
  Topic: 'Topics',
  Milestone: 'Milestones',
  Insight: 'Insights',
  Question: 'Questions',
  Source: 'Sources',
};

const KNOWN_TYPES = Object.keys(NODE_TYPE_LABELS);

/** Get a consistent color for an entity type based on its name */
function getEntityTypeColor(typeName) {
  if (NODE_COLORS[typeName]) return NODE_COLORS[typeName];
  let hash = 0;
  for (let i = 0; i < typeName.length; i++) {
    hash = typeName.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ENTITY_COLORS[Math.abs(hash) % ENTITY_COLORS.length];
}

export { NODE_COLORS, ENTITY_COLORS, getEntityTypeColor };

export default function FilterPanel({ filters, onFiltersChange, onSearch, onReset, stats, loading, entityTypes }) {
  const [searchText, setSearchText] = useState('');
  const [searchType, setSearchType] = useState('');
  const debounceRef = useRef(null);

  // Merge known types with dynamic entity types
  const allTypes = useMemo(() => {
    const types = [...KNOWN_TYPES];
    if (entityTypes) {
      for (const et of entityTypes) {
        if (!types.includes(et)) types.push(et);
      }
    }
    return types;
  }, [entityTypes]);

  const handleSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (value.trim()) {
        onSearch({ q: value.trim(), type: searchType || undefined });
      } else {
        onReset();
      }
    }, 400);
  };

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    if (searchText.trim()) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onSearch({ q: searchText.trim(), type: type || undefined });
      }, 200);
    }
  };

  const toggleNodeType = (type) => {
    const current = filters.nodeTypes;
    const next = current.includes(type)
      ? current.filter((t) => t !== type)
      : [...current, type];
    onFiltersChange({ ...filters, nodeTypes: next });
  };

  const handleReset = () => {
    setSearchText('');
    setSearchType('');
    onReset();
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div className="w-60 shrink-0 border-r bg-card/50 flex flex-col h-full overflow-y-auto">
      {/* Search */}
      <div className="p-3 border-b space-y-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm bg-background border rounded-md focus:outline-none focus:ring-1 focus:ring-primary text-foreground placeholder:text-muted-foreground"
          />
        </div>
        <select
          value={searchType}
          onChange={(e) => handleSearchTypeChange(e.target.value)}
          className="w-full px-2 py-1.5 text-xs bg-background border rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="">All types</option>
          {allTypes.map((type) => (
            <option key={type} value={type}>
              {NODE_TYPE_LABELS[type] || type}
            </option>
          ))}
        </select>
      </div>

      {/* Node Type Filters */}
      <div className="p-3 border-b space-y-1">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
          Node Types
        </p>
        {allTypes.map((type) => {
          const active = filters.nodeTypes.includes(type);
          const color = NODE_COLORS[type] || getEntityTypeColor(type);
          const label = NODE_TYPE_LABELS[type] || type;
          return (
            <label
              key={type}
              className="flex items-center gap-2 py-1 px-1 rounded hover:bg-accent/50 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={active}
                onChange={() => toggleNodeType(type)}
                className="sr-only"
              />
              <span
                className="w-3 h-3 rounded-full shrink-0 border"
                style={{
                  backgroundColor: active ? color : 'transparent',
                  borderColor: color,
                }}
              />
              <span className={active ? 'text-foreground' : 'text-muted-foreground'}>
                {label}
              </span>
              {stats && stats[type.toLowerCase()] !== undefined && (
                <span className="ml-auto text-xs text-muted-foreground">
                  {stats[type.toLowerCase()] ?? ''}
                </span>
              )}
            </label>
          );
        })}
      </div>

      {/* Stats */}
      {stats && (
        <div className="p-3 border-b space-y-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
            Graph Stats
          </p>
          <div className="grid grid-cols-2 gap-1 text-xs">
            {Object.entries(stats)
              .filter(([, v]) => v > 0)
              .sort(([, a], [, b]) => b - a)
              .map(([key, value]) => {
                const typeKey = Object.keys(NODE_TYPE_LABELS).find(k => k.toLowerCase() === key) || key;
                const label = NODE_TYPE_LABELS[typeKey] || key;
                const color = NODE_COLORS[typeKey] || getEntityTypeColor(typeKey);
                return <StatBox key={key} label={label} value={value} color={color} />;
              })}
          </div>
        </div>
      )}

      {/* Reset */}
      <div className="p-3">
        <button
          onClick={handleReset}
          disabled={loading}
          className="flex items-center gap-1.5 w-full px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground border rounded-md hover:bg-accent/50 transition-colors disabled:opacity-50"
        >
          <RotateCcw className="h-3 w-3" />
          Reset to Overview
        </button>
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="flex items-center gap-1.5 py-0.5">
      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-muted-foreground">{label}</span>
      <span className="ml-auto font-medium text-foreground">{value ?? 0}</span>
    </div>
  );
}
